/**
 * Admin Invites Hook
 * Fetch paginated invite list with status filter for admin UI.
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface InviteListItem {
  id: number;
  email: string;
  code: string;
  role: string;
  status: 'pending' | 'used' | 'expired' | 'revoked';
  expiresAt: string;
  usedAt: string | null;
  revokedAt: string | null;
  invitedBy: string;
  invitedByName?: string;
  createdAt: string;
}

export interface UseAdminInvitesParams {
  page: number;
  limit: number;
  status?: string;
}

export interface UseAdminInvitesResult {
  invites: InviteListItem[];
  total: number;
  pages: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAdminInvites(params: UseAdminInvitesParams): UseAdminInvitesResult {
  const { authFetch } = useAuth();
  const [invites, setInvites] = useState<InviteListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.status) queryParams.set('status', params.status);

      const res = await authFetch(apiUrl(`/api/admin/invites?${queryParams}`));
      if (!res.ok) {
        throw new Error('Failed to fetch invites');
      }
      const data = await res.json();
      setInvites(data.invites);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, params.page, params.limit, params.status]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  return { invites, total, pages, loading, error, refetch: fetchInvites };
}
